import React, { useEffect } from "react";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer";
import { useTheme } from "next-themes";

interface DataDiffViewerProps {
  oldData: Record<string, unknown> | null;
  newData: Record<string, unknown> | null;
}

export const DataDiffViewer: React.FC<DataDiffViewerProps> = ({
  oldData,
  newData,
}) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  // Convert data to formatted JSON strings
  const oldJson = oldData ? JSON.stringify(oldData, null, 2) : "{}";
  const newJson = newData ? JSON.stringify(newData, null, 2) : "{}";

  // Define common styles for custom display
  const commonStyles = {
    fontSize: "12px",
    lineHeight: "1.4",
    wordBreak: "break-word",
  };

  // Define styles for light and dark themes
  const viewerStyles = {
    variables: {
      light: {
        // Line backgrounds
        codeFoldGutterBackground: "#f1f1f1", // Lighter gray for fold gutter
        codeFoldBackground: "#f8f8f8", // Very light gray for code fold area
        addedBackground: "#e6ffed", // Light green for additions
        addedColor: "#24292e", // Dark text for additions
        removedBackground: "#ffeef0", // Light red for removals
        removedColor: "#24292e", // Dark text for removals
        wordAddedBackground: "#acf2bd", // Brighter green for word diffs
        wordRemovedBackground: "#fdb8c0", // Brighter red for word diffs
      },
      dark: {
        // Line backgrounds
        codeFoldGutterBackground: "#2a2d32", // Darker gray for fold gutter
        codeFoldBackground: "#212429", // Very dark gray for code fold area
        addedBackground: "#163021", // Dark green for additions
        addedColor: "#c9d1d9", // Light text for additions
        removedBackground: "#381f22", // Dark red for removals
        removedColor: "#c9d1d9", // Light text for removals
        wordAddedBackground: "#1f4d2c", // Darker green for word diffs
        wordRemovedBackground: "#622025", // Darker red for word diffs
      },
    },
    // Common styles
    diffContainer: {
      ...commonStyles,
    },
    line: {
      // Default line styling
      ...(isDark
        ? {
            backgroundColor: "#ffffff", // White background for lines in dark mode
            color: "#000000", // Black text for lines in dark mode
          }
        : {
            backgroundColor: "#ffffff", // White background for lines in light mode
            color: "#24292e", // Dark gray text for lines in light mode
          }),
    },
    marker: {
      // +/- signs
      width: "15px",
      paddingLeft: "5px",
      paddingRight: "5px",
    },
    gutter: {
      // Line numbers
      width: "35px",
      paddingLeft: "5px",
      paddingRight: "5px",
      minWidth: "35px", // Ensure line numbers have enough space
    },
    code: {
      wordBreak: "break-word", // Allow long lines to wrap
      ...commonStyles,
    },
    content: {
      ...(isDark
        ? {
            color: "#000000", // Ensure content text is black in dark mode
          }
        : {}),
    },
  };

  return (
    <div className="border-border bg-muted/30 overflow-hidden rounded-md border p-1">
      <ReactDiffViewer
        oldValue={oldJson}
        newValue={newJson}
        splitView={true} // Show side-by-side comparison
        compareMethod={DiffMethod.LINES} // Use line-based comparison
        useDarkTheme={isDark}
        styles={viewerStyles} // Apply custom theme styles
        hideLineNumbers={false}
        showDiffOnly={false} // Show the full file content
      />
    </div>
  );
};
